import { STORE_KEYS } from "./StoreKeys";
import TaskContextMenu from "@/react/components/entityViews/contextMenus/task/TaskContextMenu";
import ResourceGeneralTab from "@/bryntum-lib/editors/ResourceEditorV2/ResourceGeneralTab";
import ResourceNotifyTab from "@/bryntum-lib/editors/ResourceEditorV2/ResourceNotifyTab";
import ResourceContactTab from "@/bryntum-lib/editors/ResourceEditorV2/ResourceContactTab";
import ResourceFilesTab from "@/bryntum-lib/editors/ResourceEditorV2/ResourceFilesTab";
import ResourceNotesTab from "@/bryntum-lib/editors/ResourceEditorV2/ResourceNotesTab";
import ResourceRemindersTab from "@/bryntum-lib/editors/ResourceEditorV2/ResourceRemindersTab";
import ResourceChecklistTab from "@/bryntum-lib/editors/ResourceEditorV2/ResourceChecklistTab";
import ResourceTaskHistoryTab from "@/bryntum-lib/editors/ResourceEditorV2/ResourceTaskHistoryTab";
import ResourceContextMenu from "@/react/components/entityViews/contextMenus/resource/ResourceContextMenu";
import ClientsGeneralTab from "@/bryntum-lib/editors/clientsEditorV2/ClientsGeneralTab";
import ClientNotifyTab from "@/bryntum-lib/editors/clientsEditorV2/ClientNotifyTab";
import ClientContactTab from "@/bryntum-lib/editors/clientsEditorV2/ClientContactTab";
import ClientFilesTab from "@/bryntum-lib/editors/clientsEditorV2/ClientFilesTab";
import ClientNotesTab from "@/bryntum-lib/editors/clientsEditorV2/ClientNotesTab";
import ClientRemindersTab from "@/bryntum-lib/editors/clientsEditorV2/ClientRemindersTab";
import ClientChecklistTab from "@/bryntum-lib/editors/clientsEditorV2/ClientChecklistTab";
import ClientTaskHistoryTab from "@/bryntum-lib/editors/clientsEditorV2/ClientsTaskHistoryTab";
import ClientContextMenu from "@/react/components/entityViews/contextMenus/client/ClientContextMenu";
import { EntityDashboardView } from "@/react/components/entityManager/EntityDashboardView";
import { EntityRecord } from "@/types/entity";
import { ENTITY_TYPES, SETTING_TAB_WIDGET_KEYS } from "@/other/constants/OtherKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { ArrowUpRight } from "lucide-react";
import { GRANTS } from "@/other/constants/PrivilegeTypes";

export type EntityTypeKey = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

interface TabCallbackParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  widget: any;
  isFirstShow: boolean;
  record: EntityRecord;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: any;
}

interface ReactTabConfig {
  label: string;
  icon: string;
  type?: never;
  Tab: React.ElementType;
  onDependencyChange?: never;
  onVisible?: never;
}

interface BryntumTabConfig {
  label: string;
  icon: string;
  type: "bryntum";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Tab: any;
  onDependencyChange?: (params: TabCallbackParams) => void;
  onVisible?: (params: TabCallbackParams) => void;
}

type TabConfig = ReactTabConfig | BryntumTabConfig;

// Base pill item config
interface BasePillItemConfig {
  label: string;
  placeholder?: string;
}

// Combobox pill item (default)
interface ComboboxPillItemConfig extends BasePillItemConfig {
  type?: "combobox";
  storeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFn: (record: any) => boolean;
  showBadges?: boolean;
  multiSelect?: boolean;
  fieldConfig: {
    name: string;
    valueField: string;
    displayField: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  disabled?: (record: EntityRecord, context: any) => boolean;
  createOnUnmatched?: (params: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorHandler?: any;
    value?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    record?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    project?: any;
  }) => void;
  optionItemActionRenderer?: (params: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    record: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorHandler: any;
  }) => React.ReactNode;
}

// Input field pill item
interface InputPillItemConfig extends BasePillItemConfig {
  type: "input";
  fieldName: string;
  inputType?: "text" | "number";
}

// Icon field pill item
interface IconPillItemConfig extends BasePillItemConfig {
  type: "icon";
  storeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFn: (record: any) => boolean;
  fieldConfig: {
    name: string;
    valueField: string;
    displayField: string;
  };
}

export type PillItemConfig = ComboboxPillItemConfig | InputPillItemConfig | IconPillItemConfig;

export const PILL_ITEMS_MAP: { [K in EntityTypeKey]: PillItemConfig[] } = {
  [ENTITY_TYPES.TASK]: [
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Status",
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({editorHandler}) => {
        editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: SETTING_TAB_WIDGET_KEYS.taskStatusTab }, { by: "recordType", useExisting: true });
      }
    },
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Category",
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({editorHandler}) => {
        editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: SETTING_TAB_WIDGET_KEYS.taskCategoryTab }, { by: "recordType", useExisting: true });
      }
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Team",
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatched: ({editorHandler}) => {
        editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: SETTING_TAB_WIDGET_KEYS.teamsTab }, { by: "recordType", useExisting: true });
      }
    },
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Phase",
      label: "Phase",
      placeholder: "Phase",
      showBadges: true,
      fieldConfig: {
        name: "phaseId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({editorHandler}) => {
        editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: SETTING_TAB_WIDGET_KEYS.taskPhaseTab }, { by: "recordType", useExisting: true });
      }
    },
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      placeholder: "Tags",
      multiSelect: true,
      showBadges: true,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({editorHandler}) => {
        editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: SETTING_TAB_WIDGET_KEYS.taskTagsTab }, { by: "recordType", useExisting: true });
      }
    },
    {
      storeKey: STORE_KEYS.clientStore,
      filterFn: () => true,
      label: "Client",
      placeholder: "Client",
      showBadges: true,
      fieldConfig: {
        name: "clientId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatched: ({value, record, project}) => {
        const newRecord = project.clientsStore.createRecord({
          name: value
        });
        project.clientsStore.add(newRecord);
        record.set("clientId", newRecord.id);
      },
      optionItemActionRenderer: ({record, editorHandler}) => {
        return <button type="button" className="opacity-0 group-hover/command-item:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-sm p-1 transition-opacity" onClick={(e) => {
          e.stopPropagation();
          editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, record, { client: record });
        }}>
        <ArrowUpRight className="size-3" />
          </button>
      }
    },
    {
      storeKey: STORE_KEYS.taskBudgetStore,
      filterFn: () => true,
      disabled: (_record, context) => !context.user.can(GRANTS.grant_two),
      label: "Budget",
      placeholder: "Budget",
      showBadges: true,
      fieldConfig: {
        name: "budgetId",
        valueField: "id",
        displayField: "budgetTitleDisplayValue"
      },
      createOnUnmatched: ({editorHandler, project}) => {
        const newRecord = project.taskBudgetStore.createRecord();
        editorHandler.openEditor(EDITOR_KEYS.budgetEditor, newRecord, { budget: newRecord });
      },
      optionItemActionRenderer: ({record, editorHandler}) => {
        return <button type="button" className="opacity-0 group-hover/command-item:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-sm p-1 transition-opacity" onClick={(e) => {
          e.stopPropagation();
          editorHandler.openEditor(EDITOR_KEYS.budgetEditor, record, { budget: record });
        }}>
        <ArrowUpRight className="size-3" />
          </button>
      }
    }
  ],
  [ENTITY_TYPES.RESOURCE]: [
    {
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Status",
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      }
    },
    {
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Category",
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      }
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Team",
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      }
    },
    {
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      placeholder: "Tags",
      multiSelect: true,
      showBadges: true,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      }
    },
    {
      type: "icon",
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Icon",
      label: "Icon",
      placeholder: "Icon",
      fieldConfig: {
        name: "iconId",
        valueField: "id",
        displayField: "tagValue"
      }
    },
    {
      storeKey: STORE_KEYS.resourceTypeStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Type",
      placeholder: "Type",
      showBadges: true,
      fieldConfig: {
        name: "typeId",
        valueField: "id",
        displayField: "description"
      }
    },
    {
      type: "input",
      label: "Sub Type",
      placeholder: "Add Sub Type",
      fieldName: "jobTitle",
      inputType: "text"
    }
  ],
  [ENTITY_TYPES.CLIENT]: [
    {
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Status",
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      }
    },
    {
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Category",
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      }
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Team",
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      }
    },
    {
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      placeholder: "Tags",
      multiSelect: true,
      showBadges: true,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      }
    }
  ]
};

export interface EntityConfig {
  storeKey: string;
  tabs: TabConfig[];
  contextMenu?: React.ElementType;
  pillItems: PillItemConfig[];
}

export const ENTITY_CONFIGS: Record<EntityTypeKey, EntityConfig> = {
  [ENTITY_TYPES.TASK]: {
    storeKey: STORE_KEYS.taskStore,
    contextMenu: TaskContextMenu,
    pillItems: PILL_ITEMS_MAP.task,
    tabs: [
      {
        label: "Dashboard",
        icon: "fa-regular fa-house",
        Tab: EntityDashboardView
      },
    ]
  },
  [ENTITY_TYPES.RESOURCE]: {
    storeKey: STORE_KEYS.resourceStore,
    contextMenu: ResourceContextMenu,
    pillItems: PILL_ITEMS_MAP.resource,
    tabs: [
      {
        label: "Dashboard",
        icon: "fa-regular fa-house",
        Tab: EntityDashboardView
      },
      {
        label: "General",
        icon: "fa-regular fa-memo",
        type: "bryntum",
        Tab: ResourceGeneralTab,
        onDependencyChange: ({ widget, isFirstShow, record, context }) => {
          if (!isFirstShow) {
            widget.loadRecord(record, context, { isFirstShow });
          }
        }
      },
      {
        label: "Ownership",
        icon: "fa-regular fa-envelope",
        type: "bryntum",
        Tab: ResourceNotifyTab
      },
      {
        label: "Contacts",
        icon: "fa-regular fa-user",
        type: "bryntum",
        Tab: ResourceContactTab,
        onDependencyChange: ({ widget, isFirstShow }) => {
          if (!isFirstShow) {
            widget.loadData();
          }
        }
      },
      {
        label: "Files",
        icon: "fa-regular fa-image",
        type: "bryntum",
        Tab: ResourceFilesTab,
        onVisible: ({ widget, isFirstShow, record, context }) => {
          const { galleryView } = widget.widgetMap;
          if (widget.currentView === "gallery" && (isFirstShow || record.companyCamProject !== galleryView.swivuCCProject)) {
            /* Reconstructing images when switching to files tab as some layout is affected when switching to files tab */
            galleryView.loadRecord(record, context, galleryView.otherParams);
            galleryView.swivuCCProject = record.companyCamProject;
          }
        }
      },
      {
        label: "Notes",
        icon: "fa-regular fa-note-sticky",
        type: "bryntum",
        Tab: ResourceNotesTab
      },
      {
        label: "Reminders",
        icon: "fa-regular fa-bell",
        type: "bryntum",
        Tab: ResourceRemindersTab
      },
      {
        label: "Checklists",
        icon: "fa-regular fa-square-check",
        type: "bryntum",
        Tab: ResourceChecklistTab
      },
      {
        label: "Task History",
        icon: "fa-regular fa-clock",
        type: "bryntum",
        Tab: ResourceTaskHistoryTab
      }
    ]
  },
  [ENTITY_TYPES.CLIENT]: {
    storeKey: STORE_KEYS.clientStore,
    contextMenu: ClientContextMenu,
    pillItems: PILL_ITEMS_MAP.client,
    tabs: [
      {
        label: "Dashboard",
        icon: "fa-regular fa-house",
        Tab: EntityDashboardView
      },
      {
        label: "General",
        icon: "fa-regular fa-memo",
        type: "bryntum",
        Tab: ClientsGeneralTab,
        onDependencyChange: ({ widget, isFirstShow, record, context }) => {
          if (!isFirstShow) {
            widget.loadRecord(record, context, { isFirstShow });
          }
        }
      },
      {
        label: "Ownership",
        icon: "fa-regular fa-envelope",
        type: "bryntum",
        Tab: ClientNotifyTab
      },
      {
        label: "Contacts",
        icon: "fa-regular fa-user",
        type: "bryntum",
        Tab: ClientContactTab,
        onDependencyChange: ({ widget, isFirstShow }) => {
          if (!isFirstShow) {
            widget.loadData();
          }
        }
      },
      {
        label: "Files",
        icon: "fa-regular fa-image",
        type: "bryntum",
        Tab: ClientFilesTab,
        onVisible: ({ widget, isFirstShow, record, context }) => {
          const { galleryView } = widget.widgetMap;
          if (widget.currentView === "gallery" && (isFirstShow || record.companyCamProject !== galleryView.swivuCCProject)) {
            /* Reconstructing images when switching to files tab as some layout is affected when switching to files tab */
            galleryView.loadRecord(record, context, galleryView.otherParams);
            galleryView.swivuCCProject = record.companyCamProject;
          }
        }
      },
      {
        label: "Notes",
        icon: "fa-regular fa-note-sticky",
        type: "bryntum",
        Tab: ClientNotesTab
      },
      {
        label: "Reminders",
        icon: "fa-regular fa-bell",
        type: "bryntum",
        Tab: ClientRemindersTab
      },
      {
        label: "Checklists",
        icon: "fa-regular fa-square-check",
        type: "bryntum",
        Tab: ClientChecklistTab
      },
      {
        label: "Task History",
        icon: "fa-regular fa-clock",
        type: "bryntum",
        Tab: ClientTaskHistoryTab
      }
    ]
  }
};
